import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import "./quizSettingsPage.css";
import { fetchThemeData } from "../../themeSlice";
import { selectAdminToken } from "../../sessionSlice";
import { useNavigate } from "react-router-dom";
import { updateThemeData } from "../../functions/updateThemeData";
import WmlAvatarSettings from "../wmlAvatarSettings/WmlAvatarSettings";

const WML_POINTS_MAX = 100;
const QUESTION_TIMER_MAX_SECONDS = 300;

const QuizSettingsPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const adminToken = useSelector(selectAdminToken);
  const { currentTheme, data: themeData } = useSelector((state) => state.theme);

  const [winnerPoint, setWinnerPoint] = useState("1");
  const [majorityPoints, setMajorityPoints] = useState("0");
  const [wmlQuestionTimerSeconds, setWmlQuestionTimerSeconds] = useState("10");

  useEffect(() => {
    if (!themeData) return;
    const rawWinner = parseInt(themeData.winner_point ?? themeData.correctPoints, 10);
    const winner = Number.isNaN(rawWinner)
      ? 1
      : Math.min(WML_POINTS_MAX, Math.max(0, rawWinner));
    setWinnerPoint(String(winner));
    const rawMajority = parseInt(
      themeData.majority_points ?? themeData.wrongPoints ?? 0,
      10
    );
    const majority = Number.isNaN(rawMajority)
      ? 0
      : Math.min(WML_POINTS_MAX, Math.max(0, rawMajority));
    setMajorityPoints(String(majority));
    const t = parseInt(themeData.wmlQuestionTimerSeconds, 10);
    const ts = Number.isNaN(t)
      ? 10
      : Math.min(QUESTION_TIMER_MAX_SECONDS, Math.max(10, t));
    setWmlQuestionTimerSeconds(String(ts));
  }, [themeData, currentTheme]);

  useEffect(() => {
    dispatch(fetchThemeData({ themeId: currentTheme }));
  }, [dispatch, currentTheme]);

  const handleSubmit = async () => {
    const sanitize = (v, def, min = null, max = null) => {
      const n = parseInt(v, 10);
      if (Number.isNaN(n)) return def;
      if (min !== null && n < min) return min;
      if (max !== null && n > max) return max;
      return n;
    };

    const winnerPointToSend = sanitize(
      winnerPoint,
      1,
      0,
      WML_POINTS_MAX
    );
    const majorityPointsToSend = sanitize(
      majorityPoints,
      0,
      0,
      WML_POINTS_MAX
    );
    const timerSec = sanitize(
      wmlQuestionTimerSeconds,
      10,
      10,
      QUESTION_TIMER_MAX_SECONDS
    );

    const payload = {
      data: {
        winner_point: winnerPointToSend,
        majority_points: majorityPointsToSend,
        wmlQuestionTimerSeconds: String(timerSec),
      },
      currentTheme,
    };

    try {
      await updateThemeData({ payload, token: adminToken });
      Swal.fire("Success", "Settings saved!", "success");
      navigate("/admin/quiz-settings");
      window.location.reload();
    } catch (e) {
      Swal.fire("Error", e.message || "Failed to save settings", "error");
    }
  };

  return (
    <>
      <div className="back-button-holder">
        <button
          className="back-button"
          onClick={() => navigate("/admin/questions")}
        >
          <i class="fa-solid fa-arrow-left"></i> Back
        </button>
      </div>
      <div className="rules-page-new">
        <div className="blue-tabs-container">
          <div className="blue-tabs"></div>
          <div className="blue-tabs"></div>
          <div className="blue-tabs blue-tabs-active"></div>
        </div>

        <div className="admin-tabs-holder-v2">
          <div className="tabs-main-title">Settings</div>
          <div className="admin-tab-container">
            <div className="admin-tab-title">
              <i class="fa-solid fa-gear"></i> &nbsp; Points
            </div>
            <div className="form-group-v1">
              <label>Winner points</label>
              <input
                type="number"
                min="0"
                max={WML_POINTS_MAX}
                value={winnerPoint}
                onChange={(e) => {
                  const raw = e.target.value;
                  if (raw === "") {
                    setWinnerPoint("");
                    return;
                  }
                  const n = parseInt(raw, 10);
                  if (Number.isNaN(n)) return;
                  setWinnerPoint(
                    String(Math.min(WML_POINTS_MAX, Math.max(0, n)))
                  );
                }}
                onBlur={() => {
                  if (winnerPoint === "") setWinnerPoint("1");
                }}
              />
              <div className="code">
              Points awarded to the player who receives the most votes. Maximum limit: {WML_POINTS_MAX}.
              </div>
            </div>
            <div className="form-group-v1">
              <label>Majority points</label>
              <input
                type="number"
                min="0"
                max={WML_POINTS_MAX}
                value={majorityPoints}
                onChange={(e) => {
                  const raw = e.target.value;
                  if (raw === "") {
                    setMajorityPoints("");
                    return;
                  }
                  const n = parseInt(raw, 10);
                  if (Number.isNaN(n)) return;
                  setMajorityPoints(
                    String(Math.min(WML_POINTS_MAX, Math.max(0, n)))
                  );
                }}
                onBlur={() => {
                  if (majorityPoints === "") setMajorityPoints("0");
                }}
              />
              <div className="code">
                Points awarded to players who voted for the majority-picked player. Maximum limit: {WML_POINTS_MAX}.
              </div>
            </div>
            <div className="form-group-v1">
              <label>Question timer (seconds)</label>
              <input
                type="number"
                min="10"
                max={QUESTION_TIMER_MAX_SECONDS}
                value={wmlQuestionTimerSeconds}
                onChange={(e) => {
                  const raw = e.target.value;
                  if (raw === "") {
                    setWmlQuestionTimerSeconds("");
                    return;
                  }
                  if (!/^\d+$/.test(raw)) return;
                  setWmlQuestionTimerSeconds(raw);
                }}
                onBlur={() => {
                  const n = parseInt(wmlQuestionTimerSeconds, 10);
                  if (Number.isNaN(n)) {
                    setWmlQuestionTimerSeconds("10");
                    return;
                  }
                  setWmlQuestionTimerSeconds(
                    String(Math.min(QUESTION_TIMER_MAX_SECONDS, Math.max(10, n)))
                  );
                }}
              />
              <div className="code">
                Set between 10–{QUESTION_TIMER_MAX_SECONDS} seconds. When enabled, a countdown starts with each question and results are auto-revealed when time runs out.
              </div>
            </div>
          </div>

          <div className="admin-tab-container">
            <div className="admin-tab-title">
              <i class="fa-solid fa-user"></i> &nbsp; Who is most likely —
              characters
            </div>
            <WmlAvatarSettings embedded />
          </div>

          <div className="rules-action-holder">
            <button className="save-and-continue" onClick={handleSubmit}>
              Save Settings
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default QuizSettingsPage;
